import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ECOM_WEBSTORE_PAGE_CONFIG,
  ECOM_WEBSTORE_PAGE_CONFIG_LIST
} from '../components/content/webstore/pages/wsPageConfig';
import { Context as StoreUserContext } from '../providers/StoreUserContextProvider';
import { Context as HelperContext } from '../providers/HelperContextProvider';
import {
  ECOM_PRODUCT_STATUS_ENUM,
  ECOM_WEBSTORE_PAGE_TYPE_ENUM,
  HEADING_SIZE_STYLE_ENUM
} from '../config/constants';
import { triggerNotification } from './notification';
import { ECOM_WEBSTORE_SECTIONS } from '../components/content/webstore/sections/wsSectionConfig';
import { IMAGE_HEIGHT_ENUM } from '../components/content/webstore/sections/imageBanner/wsImageBannerConfig';
import { ECOM_WEBSTORE_SECTION_BLOCKS } from '../components/content/webstore/blocks/sectionBlockConfig';
import { PRODUCT_CATEGORY_LIST } from '../config/productCategories';
import { capitalizeFirstLetter, sterilizeUrlHandle } from './format';
import { webstoreTrackingHelper } from './webstoreTrackingHelper';

const determineEcomStoreMenuItemUrl = ({
  fkEcomStorePageType,
  ecomResourceId,
  customUrl,
  ecomStoreTheme
}) => {
  if (fkEcomStorePageType) {
    let page = ECOM_WEBSTORE_PAGE_CONFIG[fkEcomStorePageType];
    if (!page) {
      page = ECOM_WEBSTORE_PAGE_CONFIG[ECOM_WEBSTORE_PAGE_TYPE_ENUM.NOT_FOUND];
    }

    const { path, isSelected, getResourceValue } = page;
    const resourceValue = ecomResourceId
      ? getResourceValue
        ? getResourceValue(ecomResourceId, ecomStoreTheme)
        : ecomResourceId
      : '';

    const currentPath = path.replace(
      '/{id}',
      resourceValue ? `/${resourceValue}` : ''
    );

    return {
      url: '',
      path: currentPath,
      target: null,
      isSelected: isSelected(currentPath)
        ? isSelected
        : ECOM_WEBSTORE_PAGE_CONFIG_LIST.find((f) => f.isSelected(currentPath))
            ?.isSelected || isSelected
    };
  }
  return {
    url: customUrl?.replace(/^(https?:\/\/)?(www\.)?/, 'https://') ?? '',
    path: '',
    target: '_blank',
    isSelected: () => false
  };
};

const getDefaultBannerHeight = (
  config,
  adaptImageHeight,
  adaptImageWidth,
  isTabletOrSmaller,
  viewPortAdjustment,
  pageSize = null,
  pageAdjustment = null
) => {
  if (
    config.imageHeight === IMAGE_HEIGHT_ENUM.ADAPT_TO_IMAGE &&
    adaptImageHeight &&
    adaptImageWidth
  ) {
    const windowWidth = viewPortAdjustment
      ? `calc(100vw - ${viewPortAdjustment})`
      : `100vw`;
    return `calc((${adaptImageHeight} / ${adaptImageWidth}) * ${
      pageSize ? `min(${windowWidth}, ${pageSize}px)` : windowWidth
    }${pageAdjustment ? ` - ${pageAdjustment}` : ''})`;
  }

  const sizeMap = {
    [IMAGE_HEIGHT_ENUM.EXTRA_SMALL]: isTabletOrSmaller ? 200 : 300,
    [IMAGE_HEIGHT_ENUM.SMALL]: isTabletOrSmaller ? 300 : 450,
    [IMAGE_HEIGHT_ENUM.MEDIUM]: isTabletOrSmaller ? 450 : 600,
    [IMAGE_HEIGHT_ENUM.LARGE]: isTabletOrSmaller ? 550 : 750,
    [IMAGE_HEIGHT_ENUM.CUSTOM]: `${config.customImageHeight}px`
  };

  return sizeMap[config.imageHeight] || sizeMap[IMAGE_HEIGHT_ENUM.EXTRA_SMALL];
};

const sortVariantsByOptions = (ecomVendorProductVariants) =>
  ecomVendorProductVariants.sort((a, b) => {
    const aOptionsSorted = a.ecomVendorProductVariantOptions.sort(
      (a1, b1) => a1.nameSort - b1.nameSort
    );
    const bOptionsSorted = b.ecomVendorProductVariantOptions.sort(
      (a1, b1) => a1.nameSort - b1.nameSort
    );

    return (
      (aOptionsSorted[0]?.valueSort ?? 0) -
        (bOptionsSorted[0]?.valueSort ?? 0) ||
      (aOptionsSorted[1]?.valueSort ?? 0) -
        (bOptionsSorted[1]?.valueSort ?? 0) ||
      (aOptionsSorted[2]?.valueSort ?? 0) - (bOptionsSorted[2]?.valueSort ?? 0)
    );
  });

const getVariantOptions = (
  ecomVendorProductVariants,
  { beginningColor = null } = {}
) => {
  const beginningColorLower = beginningColor?.toLowerCase() || null;

  const variantOptions = sortVariantsByOptions(ecomVendorProductVariants || [])
    .reduce((r, c) => {
      [...c.ecomVendorProductVariantOptions]
        .sort(
          (a, b) => a.valueSort - b.valueSort || a.value.localeCompare(b.value)
        )
        .forEach((op) => {
          const isColorOption = op.name.toLowerCase() === 'color';
          const optionData = {
            value: op.value,
            sort: op.valueSort,
            ...(isColorOption
              ? {
                  primaryColor: c.primaryColor,
                  secondaryColor: c.secondaryColor
                }
              : {})
          };

          const existingOption = r.find((f) => f.name === op.name);
          if (existingOption) {
            if (!existingOption.values.find((f) => f.value === op.value)) {
              if (
                beginningColor &&
                optionData.value.toLowerCase() === beginningColorLower
              ) {
                existingOption.values.unshift(optionData);
              }
              else {
                existingOption.values.push(optionData);
              }
            }
          }
          else {
            r.push({
              sort: op.nameSort,
              name: op.name,
              values: [optionData]
            });
          }
        });
      return r;
    }, [])
    .sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name));

  return variantOptions;
};

const isCartVariantUnavailable = (latestVariant) =>
  latestVariant.deleted ||
  latestVariant.ecomVendorProductVariant.deleted ||
  latestVariant.ecomStoreProduct.deleted ||
  latestVariant.ecomStoreProduct.ecomVendorProduct.deleted ||
  latestVariant.ecomStoreProduct.fkEcomProductStatus.toString() !==
    ECOM_PRODUCT_STATUS_ENUM.ACTIVE.toString() ||
  latestVariant.ecomStoreProduct.ecomVendorProduct.fkEcomProductStatus.toString() !==
    ECOM_PRODUCT_STATUS_ENUM.ACTIVE.toString() ||
  (latestVariant.ecomVendorProductVariant.quantity != null &&
    latestVariant.ecomVendorProductVariant.quantity <= 0);

const isStoreVariantUnavailable = (ecomStoreProduct, selectedVariant) =>
  ecomStoreProduct?.deleted ||
  ecomStoreProduct?.ecomVendorProduct.deleted ||
  selectedVariant?.deleted ||
  selectedVariant?.unavailable ||
  selectedVariant?.ecomVendorProductVariant.deleted;

const getWebstoreResourceNavigation = (pathname) => {
  const parts = pathname.split('/').filter(Boolean);
  const result = [];

  // build lookup table of valid resource types and whether they expect an ID
  const typeMeta = {};
  const registerPath = (path) => {
    const match = path.match(/^\/([^/]+)(\/\{id\})?$/);
    if (!match) return;

    const type = match[1].toLowerCase();
    const expectsId = Boolean(match[2]);

    if (!(type in typeMeta) || expectsId) {
      typeMeta[type] = expectsId;
    }
  };

  ECOM_WEBSTORE_PAGE_CONFIG_LIST.forEach((config) => {
    registerPath(config.path);
    config.subPaths?.forEach(registerPath);
  });

  for (let i = 0; i < parts.length; ) {
    const type = parts[i].toLowerCase();

    if (!(type in typeMeta)) {
      i += 1;
    }
    else if (typeMeta[type]) {
      result.push({ type, id: parts[i + 1] || null });
      i += 2;
    }
    else {
      result.push({ type, id: null });
      i += 1;
    }
  }

  return result;
};

const getSelectedResources = (resourceNavigation, ecomStoreTheme) => {
  if (!ecomStoreTheme) {
    return {};
  }

  const finders = {
    collections: (id) =>
      ecomStoreTheme.ecomStore.ecomStoreCollections.find(
        (item) => sterilizeUrlHandle(item.name) === id
      ),
    designs: (id) =>
      ecomStoreTheme.ecomStore.ecomStoreDesigns.find(
        (item) => sterilizeUrlHandle(item.name) === id
      ),
    categories: (id) =>
      PRODUCT_CATEGORY_LIST.find(
        (item) => sterilizeUrlHandle(item.name) === id
      ),
    products: (id) =>
      ecomStoreTheme.ecomStore.ecomStoreProducts.find(
        (p) => p.urlHandle === id
      ),
    'sub-categories': (id) => {
      const product = ecomStoreTheme.ecomStore.ecomStoreProducts.find(
        (p) =>
          p.productType &&
          sterilizeUrlHandle(p.productType) === id.toLowerCase()
      );
      return {
        name: capitalizeFirstLetter(product?.productType || id.toLowerCase()),
        value: product?.productType.toLowerCase() || id.toLowerCase()
      };
    }
  };

  return resourceNavigation
    .filter((r) => !!r.id)
    .reduce((acc, curr) => {
      const finder = finders[curr.type];
      if (!finder) return acc;

      const resource = finder(curr.id);
      if (!resource) return { ...acc, selected: null };

      const typeKeyMap = {
        collections: 'ecomStoreCollection',
        designs: 'ecomStoreDesign',
        categories: 'ecomCategory',
        products: 'ecomStoreProduct',
        'sub-categories': 'ecomSubcategory'
      };

      const idKeyMap = {
        collections: 'pkEcomStoreCollection',
        designs: 'pkEcomStoreDesign',
        categories: 'pkEcomCategory',
        products: 'uuid',
        'sub-categories': 'value'
      };

      return {
        ...acc,
        [typeKeyMap[curr.type]]: { ...resource, id: curr.id, type: curr.type },
        selected: {
          type: curr.type,
          resource,
          name: resource.name || '',
          description: resource.description || '',
          id: resource[idKeyMap[curr.type]] || null
        }
      };
    }, {});
};

const getSelectedResourceBaseNavigationUrl = (
  resourceNavigation = [],
  currentType = ''
) =>
  resourceNavigation.reduce((r, c) => {
    if (c.type === currentType || !c.id) {
      return r;
    }

    switch (c.type) {
      case 'categories':
      case 'sub-categories':
      case 'designs':
      case 'collections':
        return `${r}/${c.type}/${c.id}`;
      default:
        return r;
    }
  }, '');

const getBreadcrumbsData = (resourceNavigation, ecomStoreTheme) => {
  const breadcrumbsData = [];
  let currentPath = '';

  const selectedResources = getSelectedResources(
    resourceNavigation,
    ecomStoreTheme
  );

  for (const item of resourceNavigation) {
    currentPath += `/${item.type.toLowerCase()}`;
    breadcrumbsData.push({
      title: capitalizeFirstLetter(item.type),
      path: currentPath
    });
    if (item.id) {
      currentPath += `/${item.id}`;
      const resourceName = Object.values(selectedResources).find(
        (v) => v?.id === item.id
      )?.name;

      const title = resourceName || item.id.replaceAll('-', ' ') || '';

      breadcrumbsData.push({
        title: capitalizeFirstLetter(title),
        path: currentPath
      });
    }
  }
  return breadcrumbsData;
};

const doesResourcesHaveProducts = (
  ecomStoreTheme,
  { pkEcomVendorDesign, pkEcomStoreCollection, pkEcomCategory, ecomSubcategory }
) =>
  (!pkEcomVendorDesign &&
    !pkEcomStoreCollection &&
    !pkEcomCategory &&
    !ecomSubcategory) ||
  ecomStoreTheme.ecomStore.ecomStoreProducts.some((p) => {
    if (
      (!pkEcomVendorDesign ||
        p.pkEcomVendorDesigns.includes(pkEcomVendorDesign)) &&
      (!pkEcomStoreCollection ||
        p.pkEcomStoreCollections.includes(pkEcomStoreCollection)) &&
      (!pkEcomCategory ||
        p.fkEcomCategory?.toString() === pkEcomCategory.toString()) &&
      (!ecomSubcategory ||
        p.productType?.toLowerCase() === ecomSubcategory.toLowerCase())
    ) {
      return true;
    }
    return false;
  });

const getWebstoreHeadingSize = (configHeadingSize) =>
  configHeadingSize === HEADING_SIZE_STYLE_ENUM.HUGE
    ? 50
    : configHeadingSize === HEADING_SIZE_STYLE_ENUM.EXTRA_LARGE
    ? 44
    : configHeadingSize === HEADING_SIZE_STYLE_ENUM.LARGE
    ? 36
    : configHeadingSize === HEADING_SIZE_STYLE_ENUM.MEDIUM
    ? 28
    : 22;

const getWebstoreSubHeadingSize = (configHeadingSize) =>
  configHeadingSize === HEADING_SIZE_STYLE_ENUM.HUGE
    ? 22
    : configHeadingSize === HEADING_SIZE_STYLE_ENUM.EXTRA_LARGE
    ? 20
    : configHeadingSize === HEADING_SIZE_STYLE_ENUM.LARGE
    ? 18
    : configHeadingSize === HEADING_SIZE_STYLE_ENUM.MEDIUM
    ? 16
    : 14;

const useWebstoreUserHelper = () => {
  const {
    state: helperState,
    toggleWebstoreAddProductToCartModal
  } = useContext(HelperContext);
  const {
    state,
    fetchAllProductsPreview,
    fetchEcomStoreProduct,
    fetchEcomStoreProductBulk,
    fetchEcomStoreCollectionsHistory,
    fetchEcomStoreCollections,
    fetchEcomStoreDesigns,
    fetchEcomStoreDesignsHistory
  } = useContext(StoreUserContext);

  const findOrFetchStoreProduct = (
    pkEcomStore,
    { urlHandle },
    onSuccessCallback = null,
    onErrorCallback = null
  ) => {
    const ecomStoreProduct = state.ecomStoreProductHistory.value.find(
      (f) => f.urlHandle === urlHandle
    );
    if (!ecomStoreProduct) {
      fetchEcomStoreProduct(
        pkEcomStore,
        { urlHandle },
        (data) => {
          if (onSuccessCallback) {
            onSuccessCallback(data);
          }
        },
        (e) => {
          triggerNotification(
            'There was a problem loading the product. Please try again later.'
          );
          if (onErrorCallback) {
            onErrorCallback();
          }
        }
      );
    }
    else if (onSuccessCallback) {
      onSuccessCallback(ecomStoreProduct);
    }
  };

  const viewProductOverviewModal = (
    pkEcomStore,
    { urlHandle },
    onCallback = null
  ) => {
    findOrFetchStoreProduct(
      pkEcomStore,
      { urlHandle },
      (data) => {
        toggleWebstoreAddProductToCartModal({
          isOpen: true,
          ecomStoreProduct: data
        });
        if (onCallback) {
          onCallback();
        }
      },
      () => {
        if (onCallback) {
          onCallback();
        }
      }
    );
  };

  const fetchWebstorePageResources = (
    ecomStoreTheme,
    bodySections,
    pathname,
    selectedResources
  ) => {
    // Map sections and blocks with their respective configurations
    const sectionsAndBlocksWithConfig = bodySections.map((section) => ({
      section,
      config: ECOM_WEBSTORE_SECTIONS[section.fkEcomStoreThemeSectionType],
      blocks: section.ecomStoreThemeSectionBlocks.map((block) => ({
        block,
        config:
          ECOM_WEBSTORE_SECTION_BLOCKS[block.fkEcomStoreThemeSectionBlockType]
      }))
    }));

    // Get fetch configs from blocks
    const blocksWithFetchConfigs = sectionsAndBlocksWithConfig
      .flatMap((section) => section.blocks) // Use flatMap to flatten and map in one step
      .filter((block) => block.config?.onFetchDataConfig)
      .map((block) =>
        block.config.onFetchDataConfig(
          block.block.config,
          pathname,
          selectedResources
        )
      );

    // Get fetch configs from sections
    const sectionFetchConfigs = sectionsAndBlocksWithConfig
      .filter((section) => section.config?.onFetchDataConfig)
      .map((section) =>
        section.config.onFetchDataConfig(
          section.section.config,
          pathname,
          selectedResources
        )
      );

    // Combine and reduce fetch configs by type
    const fetchTypesWithResources = [
      ...sectionFetchConfigs,
      ...blocksWithFetchConfigs
    ].reduce((result, current) => {
      const existing = result.find((fetch) => fetch.type === current.type);
      if (existing) {
        existing.resources.push(current);
      }
      else {
        result.push({ type: current.type, resources: [current] });
      }
      return result;
    }, []);

    fetchTypesWithResources.forEach((f) => {
      switch (f.type) {
        case 'ecomStoreCollection': {
          const collectionIdsThatNeedFetching = f.resources
            .map((r) => r.entityId)
            .filter((id) => !!id)
            .filter(
              (id) =>
                !state.ecomStoreCollectionHistory.value.find(
                  (c) => c.pkEcomStoreCollection.toString() === id?.toString()
                )
            );
          if (collectionIdsThatNeedFetching.length > 0) {
            fetchEcomStoreCollectionsHistory(
              ecomStoreTheme.fkEcomStore,
              collectionIdsThatNeedFetching
            );
          }
          break;
        }
        case 'ecomStoreCollections': {
          const collectionIdsThatNeedFetching = f.resources
            .map((r) => r.entityId)
            .filter((id) => !!id)
            .filter(
              (id) =>
                !state.ecomStoreCollections.value.find(
                  (c) => c.pkEcomStoreCollection.toString() === id?.toString()
                )
            );
          if (
            collectionIdsThatNeedFetching.length > 0 ||
            (ecomStoreTheme.ecomStore.ecomStoreCollections.length > 0 &&
              state.ecomStoreCollections.value.length === 0)
          ) {
            fetchEcomStoreCollections(ecomStoreTheme.ecomStore.pkEcomStore, {
              pkEcomStoreCollections: collectionIdsThatNeedFetching
            });
          }
          break;
        }
        case 'ecomStoreDesign': {
          const idsThatNeedFetching = f.resources
            .map((r) => r.entityId)
            .filter((id) => !!id)
            .filter(
              (id) =>
                !state.ecomStoreDesignHistory.value.find(
                  (c) => c.pkEcomVendorDesign.toString() === id?.toString()
                )
            );
          if (idsThatNeedFetching.length > 0) {
            fetchEcomStoreDesignsHistory(ecomStoreTheme.ecomStore.pkEcomStore, {
              pkEcomVendorDesigns: idsThatNeedFetching
            });
          }
          break;
        }
        case 'ecomStoreDesigns': {
          const idsThatNeedFetching = f.resources
            .map((r) => r.entityId)
            .filter((id) => !!id)
            .filter(
              (id) =>
                !state.ecomStoreDesignHistory.value.find(
                  (c) => c.pkEcomVendorDesign.toString() === id?.toString()
                )
            );
          if (
            idsThatNeedFetching.length > 0 ||
            (ecomStoreTheme.ecomStore.ecomStoreDesigns.length > 0 &&
              state.ecomStoreDesigns.value.length === 0)
          ) {
            fetchEcomStoreDesigns(ecomStoreTheme.ecomStore.pkEcomStore, {
              pkEcomVendorDesigns: idsThatNeedFetching
            });
          }
          break;
        }
        case 'ecomStoreProducts':
          if (
            !state.paginatedEcomStoreProductsPreview.initialized &&
            !state.paginatedEcomStoreProductsPreview.loading
          ) {
            fetchAllProductsPreview(ecomStoreTheme.fkEcomStore);
          }
          break;
        case 'ecomStoreProduct': {
          const productsThatNeedFetching = f.resources.reduce(
            (acc, r) => {
              if (
                !state.ecomStoreProductHistory.value.some(
                  (p) =>
                    p.uuid === r.entityId || p.urlHandle === r.filter?.urlHandle
                )
              ) {
                if (r.entityId) {
                  acc.uuids.push(r.entityId);
                }
                else if (r.filter?.urlHandle) {
                  acc.urlHandles.push(r.filter?.urlHandle);
                }
              }
              return acc;
            },
            {
              uuids: [],
              urlHandles: []
            }
          );
          if (
            productsThatNeedFetching.uuids.length > 0 ||
            productsThatNeedFetching.urlHandles.length > 0
          ) {
            fetchEcomStoreProductBulk(ecomStoreTheme.ecomStore.pkEcomStore, {
              uuids: productsThatNeedFetching.uuids,
              urlHandles: productsThatNeedFetching.urlHandles
            });
          }
          break;
        }
        default:
          break;
      }
    });
  };

  return {
    fetchWebstorePageResources,
    findOrFetchStoreProduct,
    viewProductOverviewModal
  };
};

const useWebstoreSearchFilter = ({
  editModeEnabled,
  onNavigate,
  search,
  pathname
}) => {
  const navigate = useNavigate();
  const filterSearchParams = new URLSearchParams(search);
  const searchParam = filterSearchParams.get('search');
  const sortParam = filterSearchParams.get('sort');
  const minPriceParam = filterSearchParams.get('minPrice');
  const maxPriceParam = filterSearchParams.get('maxPrice');
  const vendorParam = filterSearchParams.get('vendor');
  const includeInventory = filterSearchParams.get('includeInventory');
  const includeNoInventory = filterSearchParams.get('includeNoInventory');
  const pageIndexParam = filterSearchParams.get('page');

  const filter = {
    search: searchParam ?? '',
    sort: sortParam ?? null,
    minPrice: minPriceParam ?? null,
    maxPrice: maxPriceParam ?? null,
    vendor: vendorParam ?? null,
    includeInventory: includeInventory?.toLocaleLowerCase() === 'true',
    includeNoInventory: includeNoInventory?.toLocaleLowerCase() === 'true'
  };

  const hasSearched =
    !!filter.search ||
    !!filter.sort ||
    !!filter.minPrice ||
    !!filter.maxPrice ||
    !!filter.vendor ||
    !!includeInventory ||
    !!includeNoInventory;

  const resetFilter = () => {
    if (editModeEnabled) {
      onNavigate(pathname);
    }
    else {
      navigate(pathname, { replace: true });
    }
  };

  const objectToParams = (obj) => {
    const params = new URLSearchParams();
    Object.keys(obj).forEach((key) => {
      if (obj[key]) {
        params.set(key, obj[key]);
      }
    });
    return params.toString();
  };

  const onFilterChange = (params) => {
    const currentFilter = { ...filter, ...params };
    const newParams = objectToParams(currentFilter);
    if (editModeEnabled) {
      onNavigate(`${pathname}?${newParams}`);
    }
    else {
      if (!!currentFilter.search && currentFilter.search !== filter.search) {
        webstoreTrackingHelper.searchProducts(currentFilter.search);
      }

      navigate(`${pathname}?${newParams}`);
    }
  };

  return {
    filter,
    hasSearched,
    resetFilter,
    onFilterChange,
    pageIndex: pageIndexParam ? parseInt(pageIndexParam, 10) : 1
  };
};

const isVariantOptionUnavailable = (
  option,
  value,
  selectedOptions,
  ecomStoreProduct
) => {
  if (
    selectedOptions.some((so) => so.name === option.name && so.value === value)
  ) {
    return false;
  }

  const otherSelectedOptions = selectedOptions.filter(
    (so) => so.name !== option.name
  );

  const hasMatchingVariant = ecomStoreProduct.ecomStoreProductVariants.some(
    (variant) => {
      const variantOptions =
        variant.ecomVendorProductVariant.ecomVendorProductVariantOptions;

      return (
        otherSelectedOptions.every((op) =>
          variantOptions.some(
            (vo) => vo.name === op.name && vo.value === op.value
          )
        ) &&
        variantOptions.some(
          (vo) => vo.name === option.name && vo.value === value
        )
      );
    }
  );

  return !hasMatchingVariant;
};

const getSelectedEcomStoreProductVariant = (selectState, storeProducts) => {
  const product = storeProducts.find(
    (f) => f.pkEcomStoreProduct === selectState.pkEcomStoreProduct
  );

  const selectedVariant = product?.ecomStoreProductVariants.find(
    (v) =>
      selectState.selectedOptions.length ===
        v.ecomVendorProductVariant.ecomVendorProductVariantOptions.length &&
      v.ecomVendorProductVariant.ecomVendorProductVariantOptions.every((op) =>
        selectState.selectedOptions.some(
          (f) => f.name === op.name && f.value === op.value
        )
      )
  );

  const quantityLeft = selectedVariant
    ? selectedVariant.ecomVendorProductVariant.quantity
    : null;
  const outOfStock = quantityLeft !== null && quantityLeft <= 0;
  const tooManyInCart =
    quantityLeft != null && quantityLeft - selectState.count < 0;

  return {
    count: selectState.count,
    product,
    selectedVariant,
    isUnavailable: isStoreVariantUnavailable(product, selectedVariant),
    quantityLeft,
    outOfStock,
    tooManyInCart
  };
};

const getSelectedEcomStoreProductVariants = (
  storeProducts,
  ecomStoreCartProductsState
) => {
  const selectedVariants = ecomStoreCartProductsState
    .map((s) => getSelectedEcomStoreProductVariant(s, storeProducts))
    .filter((f) => !!f.selectedVariant)
    .map((v) => {
      const options =
        v.selectedVariant.ecomVendorProductVariant
          .ecomVendorProductVariantOptions;

      const firstOption = [...options].sort(
        (a, b) => a.nameSort - b.nameSort
      )[0];

      return {
        ...v,
        sortName: firstOption?.value ?? ''
      };
    })
    .sort((a, b) => a.sortName.localeCompare(b.sortName));

  return {
    selectedVariantsInfo: selectedVariants,
    selectedVariants: selectedVariants.map((v) => v.selectedVariant),
    hasUnavailableVariant: selectedVariants.some((v) => v.isUnavailable)
  };
};

const getEcomStoreProductSelectOptions = (
  ecomStoreProduct,
  ecomStoreCartProductsState = [],
  ecomStoreCartProduct = null
) => {
  const storeProducts = !ecomStoreProduct
    ? []
    : ecomStoreProduct.ecomStoreProductPackage
    ? [...ecomStoreProduct.ecomStoreProductPackage.ecomStoreProducts]
    : [ecomStoreProduct];

  const {
    selectedVariantsInfo,
    selectedVariants,
    hasUnavailableVariant
  } = getSelectedEcomStoreProductVariants(
    storeProducts,
    ecomStoreCartProductsState
  );

  const isBundleProduct =
    ecomStoreProduct && !!ecomStoreProduct.ecomStoreProductPackage;
  const isExternalProduct = storeProducts.some(
    (p) => p.ecomVendorProduct.ecomVendorProductExternalIds.length > 0
  );
  const isMissingProducts =
    ecomStoreProduct?.ecomStoreProductPackage &&
    ecomStoreProduct.ecomStoreProductPackage.ecomVendorBundle
      .ecomVendorBundleProducts.length !==
      ecomStoreProduct.ecomStoreProductPackage.ecomStoreProducts.length;

  const isUnavailable = isMissingProducts || hasUnavailableVariant;

  const productMedia = ecomStoreProduct
    ? isBundleProduct &&
      ecomStoreProduct.ecomVendorProduct.ecomVendorProductMedia.length === 0
      ? ecomStoreProduct.ecomStoreProductPackage.ecomStoreProducts
          .map(
            (p) =>
              p.ecomVendorProduct.ecomVendorProductMedia.sort(
                (a, b) => a.sort - b.sort
              )[0]
          )
          .filter(Boolean)
      : ecomStoreProduct.ecomVendorProduct.ecomVendorProductMedia
    : [];

  const storeProductOptions = storeProducts.reduce((acc, product) => {
    const variantOptions = getVariantOptions(
      product?.ecomStoreProductVariants?.map(
        (v) => v.ecomVendorProductVariant
      ) || []
    );

    const vendorVariantsSorted = sortVariantsByOptions(
      product?.ecomStoreProductVariants?.map(
        (v) => v.ecomVendorProductVariant
      ) || []
    );

    const variantsInStock = vendorVariantsSorted.filter(
      (v) =>
        !isCartVariantUnavailable({
          ecomVendorProductVariant: v,
          ecomStoreProduct: product
        })
    );
    const firstVariantWithImageAndStock = variantsInStock.find(
      (v) =>
        !!v.fkEcomVendorProductMedia &&
        product.ecomVendorProduct.ecomVendorProductMedia.some(
          (m) => m.src === v.previewImage
        )
    );
    const firstVariantInStock = variantsInStock[0];

    const defaultSelectState = {
      pkEcomStoreProduct: product.pkEcomStoreProduct,
      count: ecomStoreProduct.ecomStoreProductPackage
        ? ecomStoreProduct.ecomStoreProductPackage.ecomVendorBundle.ecomVendorBundleProducts.find(
            (p) =>
              p.fkEcomVendorProduct ===
              product.ecomVendorProduct.pkEcomVendorProduct
          )?.count ?? 1
        : 1,
      fkEcomVendorProduct: product.ecomVendorProduct.pkEcomVendorProduct,
      fkEcomStoreProductPackage:
        ecomStoreProduct.ecomStoreProductPackage?.pkEcomStoreProductPackage,
      selectedOptions: firstVariantWithImageAndStock
        ? firstVariantWithImageAndStock.ecomVendorProductVariantOptions.map(
            (op) => ({
              nameSort: op.nameSort,
              name: op.name,
              value: op.value
            })
          )
        : firstVariantInStock
        ? firstVariantInStock.ecomVendorProductVariantOptions.map((op) => ({
            nameSort: op.nameSort,
            name: op.name,
            value: op.value
          }))
        : variantOptions.map((op) => ({
            nameSort: op.nameSort,
            name: op.name,
            value: op.values[0].value
          })),
      ecomStoreCartProductInputAnswers: product.ecomStoreProductInputAnswers
        .map((a) => {
          const foundInput = product.ecomVendorProduct.ecomVendorProductInputs.find(
            (i) =>
              i.fkRegFormControlType === a.fkRegFormControlType &&
              (i.pkEcomVendorProductInput === a.fkEcomVendorProductInput ||
                i.label.toLowerCase() === a.label.toLowerCase())
          );
          return foundInput
            ? {
                isAutopopulated: true,
                fkEcomVendorProductInput: foundInput.pkEcomVendorProductInput,
                fkRegFormControlType: foundInput.fkRegFormControlType,
                file: null,
                value: a.value
              }
            : null;
        })
        .filter((f) => !!f),
      ecomOrderProductPrintImages: []
    };

    return [
      ...acc,
      {
        pkEcomStoreProduct: product.pkEcomStoreProduct,
        variantOptions,
        defaultSelectState,
        isBundleProduct,
        isExternalProduct,
        productMedia
      }
    ];
  }, []);

  return {
    storeProducts,
    storeProductOptions,
    productMedia,
    selectedVariantsInfo,
    selectedVariants,
    isBundleProduct,
    isExternalProduct,
    isUnavailable,
    isMissingProducts,
    hasUnavailableVariant
  };
};

const getSelectedEcomStoreProductDesigns = (
  ecomStoreProduct,
  pkEcomVendorProductVariant
) => {
  const mainDesigns = ecomStoreProduct.ecomVendorProduct.ecomVendorProductPrintDesigns.filter(
    (d) => d.pkEcomVendorProductVariants.length === 0
  );

  const selectedVariantDesigns = pkEcomVendorProductVariant
    ? ecomStoreProduct.ecomVendorProduct.ecomVendorProductPrintDesigns.filter(
        (d) =>
          d.pkEcomVendorProductVariants.some(
            (v) => v === pkEcomVendorProductVariant
          )
      )
    : [];

  const selectedDesigns =
    selectedVariantDesigns.length > 0 ? selectedVariantDesigns : mainDesigns;

  return {
    mainDesigns,
    selectedDesigns
  };
};

export {
  determineEcomStoreMenuItemUrl,
  getDefaultBannerHeight,
  getVariantOptions,
  isCartVariantUnavailable,
  isStoreVariantUnavailable,
  getWebstoreResourceNavigation,
  getSelectedResourceBaseNavigationUrl,
  getSelectedResources,
  getBreadcrumbsData,
  getWebstoreHeadingSize,
  getWebstoreSubHeadingSize,
  doesResourcesHaveProducts,
  sortVariantsByOptions,
  useWebstoreUserHelper,
  useWebstoreSearchFilter,
  isVariantOptionUnavailable,
  getEcomStoreProductSelectOptions,
  getSelectedEcomStoreProductVariant,
  getSelectedEcomStoreProductVariants,
  getSelectedEcomStoreProductDesigns
};
